import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Image,
  Popover,
  Portal,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import MDEditor from '@uiw/react-md-editor';
import { useEffect, useMemo, useState } from 'react';
import { FaExpand, FaFont, FaMicrophone, FaRobot } from 'react-icons/fa';
import { FiMoreVertical, FiSmile } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { useColorMode } from '#components/ui/color-mode';
import { toaster } from '#components/ui/toaster';
import type { MessageFieldsFragment } from '#graphql/generated';
import { DELETE_MESSAGE } from '#graphql/mutations/delete-message';
import { TOGGLE_MESSAGE_REACTION } from '#graphql/mutations/toggle-message-reaction';
import { UPDATE_MESSAGE } from '#graphql/mutations/update-message';
import { MESSAGE_REACTION_REACTORS } from '#graphql/queries/message-reaction-reactors';
import { useReactions } from '#providers/reactions-provider';
import { useUser } from '#providers/user-provider';
import { documentViewHref } from '#utils/document-app-path';
import { gravatarAvatarUrl, SYSTEM_GRAVATAR_HASH } from '#utils/gravatar';

function getSourceIcon(source: string) {
  switch (source) {
    case 'text':
      return FaFont;
    case 'audio':
      return FaMicrophone;
    case 'ai-note':
      return FaRobot;
    default:
      return null;
  }
}

export type MessageCardVariant = 'collapsed' | 'expanded';

function formatMessageTimestamp(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function lastChildCreatedAtIso(children: MessageFieldsFragment[]): string {
  let max = children[0]!.createdAt;
  let maxMs = new Date(max).getTime();
  for (let i = 1; i < children.length; i += 1) {
    const t = new Date(children[i]!.createdAt).getTime();
    if (t > maxMs) {
      maxMs = t;
      max = children[i]!.createdAt;
    }
  }
  return max;
}

function formatThreadReplySummary(count: number): string {
  return count === 1 ? '1 reply' : `${count} replies`;
}

function parseMessageReactionsMap(raw: unknown): Record<string, string[]> {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  const out: Record<string, string[]> = {};
  for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(val)) {
      const ids = val.filter((x): x is string => typeof x === 'string');
      if (ids.length > 0) {
        out[key] = ids;
      }
    }
  }
  return out;
}

type MessageContextRow = MessageFieldsFragment['messageContexts'][number];

/** In-app deep links derivable from `messageContexts` without extra GraphQL fields. */
function getMessageDirectReferenceLinks(
  contexts: MessageContextRow[] | undefined,
): { key: string; to: string; label: string }[] {
  const out: { key: string; to: string; label: string }[] = [];
  if (!contexts?.length) {
    return out;
  }

  for (const ctx of contexts) {
    if (ctx.name === 'documentId' && ctx.document?.path) {
      const { path } = ctx.document;
      out.push({
        key: `document:${path}`,
        to: documentViewHref(path),
        label: path,
      });
    } else if (ctx.name === 'processorInstanceId') {
      const id = ctx.value?.trim();
      if (id) {
        out.push({
          key: `processor:${id}`,
          to: `/instance/${encodeURIComponent(id)}`,
          label: 'Processor run',
        });
      }
    }
  }
  return out;
}

function MessageAuthorAvatar({
  gravatarHash,
  boxSize,
  gravatarPixelSize,
  borderRadius = 'full',
}: {
  gravatarHash: string;
  boxSize: number;
  gravatarPixelSize: number;
  /** Main message gutter uses a rounded square; small inline avatars keep `full`. */
  borderRadius?: 'full' | 'md' | 'lg';
}) {
  const isSystemBranding = gravatarHash === SYSTEM_GRAVATAR_HASH;

  return (
    <Image
      src={
        isSystemBranding
          ? '/icon.svg'
          : gravatarAvatarUrl(gravatarHash, gravatarPixelSize)
      }
      alt=""
      aria-hidden
      boxSize={`${boxSize}px`}
      borderRadius={borderRadius}
      flexShrink={0}
      objectFit={isSystemBranding ? 'contain' : 'cover'}
      p={isSystemBranding ? '2px' : undefined}
      bg={
        isSystemBranding ? 'var(--background-base-highlight-color)' : undefined
      }
    />
  );
}

interface MessageCardProps {
  message: MessageFieldsFragment;
  childMessages?: MessageFieldsFragment[];
  variant: MessageCardVariant;
  /** Collapsed: expand thread in URL. Omitted for summary rows. */
  onExpandThread?: (rootId: string) => void;
  onMessageDeleted?: () => void | Promise<void>;
}

export function MessageCard({
  message,
  childMessages,
  variant,
  onExpandThread,
  onMessageDeleted,
}: MessageCardProps) {
  const { colorMode } = useColorMode();
  const { user } = useUser();
  const [deleteMessage, { loading: deleteLoading }] =
    useMutation(DELETE_MESSAGE);
  const [updateMessage, { loading: updateLoading }] = useMutation(
    UPDATE_MESSAGE,
    {
      update(cache, { data }) {
        const updated = data?.updateMessage;
        if (!updated) return;

        const cacheId = cache.identify({
          __typename: 'Message',
          id: updated.id,
        });
        if (!cacheId) return;

        cache.modify({
          id: cacheId,
          fields: {
            value() {
              return updated.value;
            },
            updatedAt() {
              return updated.updatedAt;
            },
          },
        });
      },
    },
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');

  useEffect(() => {
    setIsEditing(false);
    setEditDraft('');
  }, [message.id]);

  const canDeleteMessage = Boolean(
    user &&
      (message.userId === user.id ||
        (message.userId === 'system' && message.targetUserId === user.id)),
  );

  const handleDeleteMessage = async () => {
    try {
      await deleteMessage({ variables: { input: { id: message.id } } });
      await onMessageDeleted?.();
      toaster.create({
        title: 'Message deleted',
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: 'Could not delete message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditDraft('');
  };

  const handleSaveEdit = async () => {
    if (!editDraft.trim()) {
      toaster.create({
        title: 'Message cannot be empty',
        type: 'error',
      });
      return;
    }

    try {
      await updateMessage({
        variables: { input: { id: message.id, value: editDraft } },
      });
      setIsEditing(false);
      setEditDraft('');
      toaster.create({
        title: 'Message updated',
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: 'Could not update message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const isSystemMessage = message.userId === 'system';

  const canEditMessage = Boolean(
    user && message.userId === user.id && !isSystemMessage,
  );

  const isRoot = message.parentMessageId == null;
  const showExpand =
    variant === 'collapsed' && isRoot && Boolean(onExpandThread);

  const directRefLinks = useMemo(
    () => getMessageDirectReferenceLinks(message.messageContexts),
    [message.messageContexts],
  );

  const showDetailsPopover =
    directRefLinks.length > 0 || canEditMessage || canDeleteMessage;

  const [detailsPopoverOpen, setDetailsPopoverOpen] = useState(false);

  return (
    <Box>
      <HStack align="flex-start" gap={3}>
        {message.author?.gravatarHash ? (
          <Box flexShrink={0} pt={1}>
            <MessageAuthorAvatar
              gravatarHash={message.author.gravatarHash}
              boxSize={32}
              gravatarPixelSize={64}
              borderRadius="md"
            />
          </Box>
        ) : null}
        <VStack align="stretch" flex={1} minW={0} gap={0}>
          <Box
            p={4}
            backgroundColor="var(--background-base-highlight-color)"
            borderRadius="md"
            borderColor="var(--border-base-color)"
            borderWidth="var(--border-base-width)"
          >
            <HStack justify="space-between" align="start" mb={2}>
              <HStack gap={2} align="center" flexWrap="wrap">
                <Text
                  fontSize="xs"
                  fontWeight="medium"
                  color={isSystemMessage ? 'var(--brand-base-color)' : 'fg'}
                >
                  {message.author?.displayName ?? 'Unknown'}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  ·
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {formatMessageTimestamp(message.createdAt)}
                </Text>
                {getSourceIcon(message.source) && (
                  <Box
                    as={getSourceIcon(message.source)!}
                    aria-label={message.source}
                    color={
                      message.source === 'ai-note'
                        ? 'var(--brand-base-color)'
                        : 'fg.subtle'
                    }
                    fontSize="xs"
                    display="inline-flex"
                  />
                )}
              </HStack>
              <ButtonGroup size="xs" variant="outline" attached align="center">
                {showExpand && onExpandThread && (
                  <IconButton
                    aria-label="Expand thread"
                    onClick={() => onExpandThread(message.id)}
                  >
                    <FaExpand />
                  </IconButton>
                )}
                {showDetailsPopover && (
                  <Popover.Root
                    open={detailsPopoverOpen}
                    onOpenChange={(e) => setDetailsPopoverOpen(e.open)}
                    positioning={{ placement: 'bottom-end' }}
                    size="sm"
                  >
                    <Popover.Trigger asChild>
                      <IconButton
                        aria-label="Message details and actions"
                        disabled={deleteLoading || updateLoading}
                      >
                        <FiMoreVertical />
                      </IconButton>
                    </Popover.Trigger>
                    <Portal>
                      <Popover.Positioner>
                        <Popover.Content
                          p={2}
                          minW="200px"
                          bg="bg"
                          border="1px solid"
                          borderColor="border"
                          shadow="md"
                        >
                          <Popover.Arrow>
                            <Popover.ArrowTip />
                          </Popover.Arrow>
                          <VStack align="stretch" gap={2}>
                            {directRefLinks.length > 0 ? (
                              <VStack align="stretch" gap={1}>
                                {directRefLinks.map((row) => (
                                  <Link
                                    key={row.key}
                                    to={row.to}
                                    style={{ textDecoration: 'none' }}
                                    onClick={() => setDetailsPopoverOpen(false)}
                                  >
                                    <Text
                                      fontSize="sm"
                                      color="blue.fg"
                                      cursor="pointer"
                                      _hover={{ textDecoration: 'underline' }}
                                    >
                                      {row.label}
                                    </Text>
                                  </Link>
                                ))}
                              </VStack>
                            ) : null}
                            {canEditMessage || canDeleteMessage ? (
                              <VStack
                                align="stretch"
                                gap={0}
                                borderTopWidth={
                                  directRefLinks.length > 0 ? '1px' : undefined
                                }
                                borderColor="border"
                                pt={directRefLinks.length > 0 ? 2 : 0}
                              >
                                {canEditMessage ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    justifyContent="flex-start"
                                    fontWeight="normal"
                                    onClick={() => {
                                      setDetailsPopoverOpen(false);
                                      setIsEditing(true);
                                      setEditDraft(message.value);
                                    }}
                                  >
                                    Edit
                                  </Button>
                                ) : null}
                                {canDeleteMessage ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    justifyContent="flex-start"
                                    fontWeight="normal"
                                    color="fg.error"
                                    onClick={() => {
                                      setDetailsPopoverOpen(false);
                                      void handleDeleteMessage();
                                    }}
                                  >
                                    Delete
                                  </Button>
                                ) : null}
                              </VStack>
                            ) : null}
                          </VStack>
                        </Popover.Content>
                      </Popover.Positioner>
                    </Portal>
                  </Popover.Root>
                )}
              </ButtonGroup>
            </HStack>
            {isEditing ? (
              <VStack align="stretch" gap={2}>
                <Textarea
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  fontSize="sm"
                  rows={6}
                  bg="var(--input-base-background-color)"
                  borderColor="var(--border-base-color)"
                  borderWidth="var(--border-base-width)"
                  color="var(--font-base-color)"
                />
                <HStack gap={2}>
                  <Button
                    size="xs"
                    colorPalette="blue"
                    onClick={() => void handleSaveEdit()}
                    loading={updateLoading}
                  >
                    Save
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={updateLoading}
                  >
                    Cancel
                  </Button>
                </HStack>
              </VStack>
            ) : (
              <Box
                data-color-mode={colorMode}
                fontSize="sm"
                maxW="100%"
                overflowX="auto"
                color="var(--font-base-color)"
              >
                <MDEditor.Markdown source={message.value} />
              </Box>
            )}
            <MessageReactionsBar message={message} />
          </Box>

          {variant === 'collapsed' &&
            childMessages &&
            childMessages.length > 0 && (
              <CollapsedThreadSummaryRow
                rootMessageId={message.id}
                childMessages={childMessages}
                onExpandThread={onExpandThread}
              />
            )}

          {variant === 'expanded' &&
            childMessages &&
            childMessages.length > 0 && (
              <VStack gap={2} align="stretch" mt={2} ml={-4}>
                {childMessages.map((child) => (
                  <MessageCard
                    key={child.id}
                    message={child}
                    variant="expanded"
                    onMessageDeleted={onMessageDeleted}
                  />
                ))}
              </VStack>
            )}
        </VStack>
      </HStack>
    </Box>
  );
}

function MessageReactionsBar({ message }: { message: MessageFieldsFragment }) {
  const { user } = useUser();
  const catalog = useReactions();
  const emojiById = useMemo(
    () => Object.fromEntries(catalog.map((r) => [r.id, r.value])),
    [catalog],
  );
  const reactionMap = parseMessageReactionsMap(message.reactions);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hoveredReactionId, setHoveredReactionId] = useState<string | null>(
    null,
  );
  const [reactorsByReactionId, setReactorsByReactionId] = useState<
    Record<
      string,
      { userId: string; displayName: string; gravatarHash?: string | null }[]
    >
  >({});
  const [reactorLoadErrors, setReactorLoadErrors] = useState<
    Record<string, string>
  >({});
  const [reactorLoadingByReactionId, setReactorLoadingByReactionId] = useState<
    Record<string, boolean>
  >({});

  const [loadReactors] = useLazyQuery(MESSAGE_REACTION_REACTORS, {
    fetchPolicy: 'network-only',
  });

  const canReact = Boolean(
    user && (message.userId === user.id || message.targetUserId === user.id),
  );

  const [toggleReaction, { loading: toggleLoading }] = useMutation(
    TOGGLE_MESSAGE_REACTION,
    {
      update(cache, { data }) {
        const updated = data?.toggleMessageReaction;
        if (!updated) return;

        const cacheId = cache.identify({
          __typename: 'Message',
          id: updated.id,
        });
        if (!cacheId) return;

        cache.modify({
          id: cacheId,
          fields: {
            reactions() {
              return updated.reactions;
            },
          },
        });
      },
    },
  );

  const handleToggle = async (reactionId: string) => {
    try {
      await toggleReaction({
        variables: {
          input: { messageId: message.id, reactionId },
        },
      });
      setPickerOpen(false);
    } catch (error) {
      toaster.create({
        title: 'Could not update reaction',
        description:
          error instanceof Error ? error.message : 'Unexpected error',
        type: 'error',
      });
    }
  };

  const activeReactionIds = Object.keys(reactionMap).filter(
    (id) => (reactionMap[id]?.length ?? 0) > 0,
  );
  activeReactionIds.sort();

  const handleReactionHover = (reactionId: string) => {
    setHoveredReactionId(reactionId);
    if (
      reactorsByReactionId[reactionId] != null ||
      reactorLoadingByReactionId[reactionId]
    ) {
      return;
    }

    setReactorLoadingByReactionId((prev) => ({ ...prev, [reactionId]: true }));
    setReactorLoadErrors((prev) => {
      const next = { ...prev };
      delete next[reactionId];
      return next;
    });
    void loadReactors({
      variables: { messageId: message.id, reactionId },
    })
      .then((result) => {
        if (result.error) {
          setReactorLoadErrors((prev) => ({
            ...prev,
            [reactionId]: result.error?.message ?? 'Failed to load reactors',
          }));
          return;
        }

        setReactorsByReactionId((prev) => ({
          ...prev,
          [reactionId]: result.data?.messageReactionReactors ?? [],
        }));
      })
      .catch((error: unknown) => {
        setReactorLoadErrors((prev) => ({
          ...prev,
          [reactionId]:
            error instanceof Error ? error.message : 'Failed to load reactors',
        }));
      })
      .finally(() => {
        setReactorLoadingByReactionId((prev) => ({
          ...prev,
          [reactionId]: false,
        }));
      });
  };

  return (
    <HStack
      mt={2}
      flexWrap="wrap"
      gap={1}
      align="center"
      w="full"
      justify="flex-start"
    >
      {activeReactionIds.map((reactionId) => {
        const userIds = reactionMap[reactionId] ?? [];
        const count = userIds.length;
        const emoji = emojiById[reactionId] ?? reactionId;
        const iReacted = Boolean(user && userIds.includes(user.id));
        const reactors = reactorsByReactionId[reactionId] ?? [];
        const loadingReactors = Boolean(reactorLoadingByReactionId[reactionId]);
        const reactorError = reactorLoadErrors[reactionId];
        return (
          <Popover.Root
            key={reactionId}
            open={hoveredReactionId === reactionId}
            positioning={{ placement: 'top-start' }}
            size="sm"
          >
            <Popover.Trigger asChild>
              <Button
                type="button"
                variant="outline"
                borderRadius="full"
                textStyle="xs"
                fontWeight="medium"
                px={2}
                py={0.5}
                minW={0}
                h="28px"
                lineHeight={1}
                disabled={toggleLoading}
                borderColor={iReacted ? 'blue.solid' : 'border'}
                bg={iReacted ? 'blue.subtle' : 'transparent'}
                _hover={{
                  bg: iReacted ? 'blue.subtle' : 'bg.subtle',
                }}
                onClick={() => void handleToggle(reactionId)}
                onMouseEnter={() => handleReactionHover(reactionId)}
                onMouseLeave={() => setHoveredReactionId(null)}
                aria-label={`Reaction ${reactionId}${iReacted ? ', you reacted' : ''}`}
              >
                <Text as="span" fontSize="md" lineHeight={1}>
                  {emoji}
                </Text>
                <Text as="span" fontSize="xs" color="fg.muted" ml={1}>
                  {count}
                </Text>
              </Button>
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner>
                <Popover.Content
                  p={2}
                  maxW="220px"
                  bg="bg"
                  border="1px solid"
                  borderColor="border"
                  shadow="md"
                >
                  <Popover.Arrow>
                    <Popover.ArrowTip />
                  </Popover.Arrow>
                  <VStack align="stretch" gap={1}>
                    {loadingReactors ? (
                      <Text fontSize="xs" color="fg.muted">
                        Loading...
                      </Text>
                    ) : reactorError ? (
                      <Text fontSize="xs" color="fg.error">
                        Could not load reactions
                      </Text>
                    ) : reactors.length === 0 ? (
                      <Text fontSize="xs" color="fg.muted">
                        No reactions yet
                      </Text>
                    ) : (
                      reactors.map((reactor) => (
                        <HStack key={reactor.userId} gap={2} align="center">
                          {reactor.gravatarHash ? (
                            <MessageAuthorAvatar
                              gravatarHash={reactor.gravatarHash}
                              boxSize={14}
                              gravatarPixelSize={28}
                            />
                          ) : null}
                          <Text fontSize="xs">
                            {reactor.displayName || reactor.userId}
                          </Text>
                        </HStack>
                      ))
                    )}
                  </VStack>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        );
      })}
      {canReact ? (
        <Popover.Root
          open={pickerOpen}
          onOpenChange={(e) => setPickerOpen(e.open)}
          positioning={{ placement: 'top-start' }}
          size="sm"
        >
          <Popover.Trigger asChild>
            <IconButton
              type="button"
              variant="ghost"
              size="xs"
              borderRadius="md"
              minW={0}
              aria-label="Add reaction"
              title="Add reaction"
              disabled={toggleLoading}
            >
              <FiSmile size={14} aria-hidden />
            </IconButton>
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner>
              <Popover.Content
                p={2}
                maxW="280px"
                bg="bg"
                border="1px solid"
                borderColor="border"
                shadow="lg"
              >
                <Popover.Arrow>
                  <Popover.ArrowTip />
                </Popover.Arrow>
                <SimpleGrid columns={{ base: 5, sm: 6 }} gap={1}>
                  {catalog.map((r) => {
                    const ids = reactionMap[r.id] ?? [];
                    const active = Boolean(user && ids.includes(user.id));
                    return (
                      <Button
                        key={r.id}
                        type="button"
                        size="sm"
                        variant={active ? 'solid' : 'ghost'}
                        fontSize="xl"
                        lineHeight={1}
                        minW="40px"
                        h="40px"
                        px={0}
                        onClick={() => void handleToggle(r.id)}
                        aria-label={`${r.id}${active ? ', remove' : ', add'}`}
                        title={r.id}
                      >
                        {r.value}
                      </Button>
                    );
                  })}
                </SimpleGrid>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
      ) : null}
    </HStack>
  );
}

function CollapsedThreadSummaryRow({
  rootMessageId,
  childMessages,
  onExpandThread,
}: {
  rootMessageId: string;
  childMessages: MessageFieldsFragment[];
  onExpandThread?: (rootId: string) => void;
}) {
  const count = childMessages.length;
  const lastIso = lastChildCreatedAtIso(childMessages);
  const lastFormatted = formatMessageTimestamp(lastIso);
  const replyPhrase = formatThreadReplySummary(count);
  const visibleLabel = `${replyPhrase} · Last reply ${lastFormatted}`;
  const ariaLabel = `Expand thread, ${replyPhrase}, last reply ${lastFormatted}`;

  const interactive = Boolean(onExpandThread);

  const summaryText = (
    <Text fontSize="xs" color="fg.muted" lineHeight="short">
      {visibleLabel}
    </Text>
  );

  return (
    <Box mt={2} pl={6}>
      {interactive ? (
        <Button
          type="button"
          variant="ghost"
          width="100%"
          justifyContent="flex-start"
          fontWeight="normal"
          minH="unset"
          h="auto"
          py={1.5}
          px={0}
          borderRadius="sm"
          color="fg.muted"
          _hover={{ bg: 'var(--background-base-highlight-color)' }}
          onClick={() => onExpandThread!(rootMessageId)}
          aria-label={ariaLabel}
        >
          {summaryText}
        </Button>
      ) : (
        <Box py={1.5} px={0}>
          {summaryText}
        </Box>
      )}
    </Box>
  );
}
